import { useEffect, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { Upload, X, Save } from "lucide-react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getSiteConfiguration, updateSiteConfiguration } from "../../api/siteConfiguration";
import { storage } from "../../services/firebase";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { Alert, Button, Card, Field, FormGrid, Input, Loader, Switch, FileButton, Textarea } from "../../ui";
import "./styles.scss";

type Props = {
  canManage: boolean;
};

const SiteConfigurationManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverImageUrl, setCoverImageUrl] = useState<string>("");
  const [originalCoverImageUrl, setOriginalCoverImageUrl] = useState<string>("");
  const [instaPayDetails, setInstaPayDetails] = useState("");
  const [originalInstaPayDetails, setOriginalInstaPayDetails] = useState("");
  // Clash Points global config (numeric fields kept as strings for raw <input> control)
  const [pointsEnabled, setPointsEnabled] = useState(true);
  const [pointsValidityMonths, setPointsValidityMonths] = useState("3");
  const [pointsToEgpRate, setPointsToEgpRate] = useState("0");
  const [minPointsToConvert, setMinPointsToConvert] = useState("0");
  const [reservationPointsPerEgp, setReservationPointsPerEgp] = useState("0");
  const [registrationPointsPerEgp, setRegistrationPointsPerEgp] = useState("0");
  // Converter preview inputs (UI-only; not persisted, not part of isDirty)
  const [convertPointsInput, setConvertPointsInput] = useState("");
  const [convertCostInput, setConvertCostInput] = useState("");
  const [originalPoints, setOriginalPoints] = useState({
    pointsEnabled: true,
    pointsValidityMonths: "3",
    pointsToEgpRate: "0",
    minPointsToConvert: "0",
    reservationPointsPerEgp: "0",
    registrationPointsPerEgp: "0",
  });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getSiteConfiguration();
      const cfg = res?.data ?? res ?? {};
      const url = cfg?.homeCoverImageUrl ?? "";
      setCoverImageUrl(url || "");
      setOriginalCoverImageUrl(url || "");
      setInstaPayDetails(cfg?.instaPayDetails ?? "");
      setOriginalInstaPayDetails(cfg?.instaPayDetails ?? "");

      const enabled = cfg?.pointsEnabled ?? true;
      const validity = String(cfg?.pointsValidityMonths ?? 3);
      const egpRate = String(cfg?.pointsToEgpRate ?? 0);
      const minConvert = String(cfg?.minPointsToConvert ?? 0);
      const resPerEgp = String(cfg?.reservationPointsPerEgp ?? 0);
      const regPerEgp = String(cfg?.registrationPointsPerEgp ?? 0);
      setPointsEnabled(enabled);
      setPointsValidityMonths(validity);
      setPointsToEgpRate(egpRate);
      setMinPointsToConvert(minConvert);
      setReservationPointsPerEgp(resPerEgp);
      setRegistrationPointsPerEgp(regPerEgp);
      setOriginalPoints({
        pointsEnabled: enabled,
        pointsValidityMonths: validity,
        pointsToEgpRate: egpRate,
        minPointsToConvert: minConvert,
        reservationPointsPerEgp: resPerEgp,
        registrationPointsPerEgp: regPerEgp,
      });
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to load site configuration" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleFileChange = async (files: FileList) => {
    const file = files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      dispatch(setGeneralAlertMessage({ type: "error", message: "Please select an image file" }));
      return;
    }
    setUploading(true);
    try {
      const fileName = `site-configuration/home-cover_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "")}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      setCoverImageUrl(url);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to upload image" }));
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setCoverImageUrl("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validityNum = parseInt(pointsValidityMonths, 10);
      const egpRateNum = parseFloat(pointsToEgpRate);
      const minConvertNum = parseInt(minPointsToConvert, 10);
      const resPerEgpNum = parseFloat(reservationPointsPerEgp);
      const regPerEgpNum = parseFloat(registrationPointsPerEgp);
      await updateSiteConfiguration({
        homeCoverImageUrl: coverImageUrl.trim() || null,
        pointsEnabled,
        pointsValidityMonths: isNaN(validityNum) ? 0 : validityNum,
        pointsToEgpRate: isNaN(egpRateNum) ? 0 : egpRateNum,
        minPointsToConvert: isNaN(minConvertNum) ? 0 : minConvertNum,
        reservationPointsPerEgp: isNaN(resPerEgpNum) ? 0 : resPerEgpNum,
        registrationPointsPerEgp: isNaN(regPerEgpNum) ? 0 : regPerEgpNum,
        instaPayDetails: instaPayDetails.trim(),
      });
      setOriginalCoverImageUrl(coverImageUrl.trim());
      setInstaPayDetails(instaPayDetails.trim());
      setOriginalInstaPayDetails(instaPayDetails.trim());
      setOriginalPoints({
        pointsEnabled,
        pointsValidityMonths,
        pointsToEgpRate,
        minPointsToConvert,
        reservationPointsPerEgp,
        registrationPointsPerEgp,
      });
      dispatch(setGeneralAlertMessage({ type: "success", message: "Site configuration updated successfully" }));
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to update site configuration" }));
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    coverImageUrl.trim() !== originalCoverImageUrl.trim() ||
    instaPayDetails.trim() !== originalInstaPayDetails.trim() ||
    pointsEnabled !== originalPoints.pointsEnabled ||
    pointsValidityMonths !== originalPoints.pointsValidityMonths ||
    pointsToEgpRate !== originalPoints.pointsToEgpRate ||
    minPointsToConvert !== originalPoints.minPointsToConvert ||
    reservationPointsPerEgp !== originalPoints.reservationPointsPerEgp ||
    registrationPointsPerEgp !== originalPoints.registrationPointsPerEgp;

  const pointsField = (
    id: string,
    label: string,
    value: string,
    onChange: (value: string) => void,
    step: string,
    help: ReactNode
  ) => (
    <Field label={label} htmlFor={id} hint={help}>
      <Input
        id={id}
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!canManage}
      />
    </Field>
  );

  return (
    <Card
      className="site-configuration-management"
      title="Site Configuration"
      subtitle="Customize the public homepage of theclash.com and where players send InstaPay payments."
      footer={
        !loading && (
          <Button
            icon={<Save size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={!canManage || uploading || !isDirty}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        )
      }
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <section className="config-section">
            <h4 className="config-label">Homepage Cover Image</h4>
            <p className="config-hint">
              Shown as the hero image on the public homepage. Recommended: landscape, at least 1920×1080.
            </p>

            <div className="cover-preview">
              {coverImageUrl ? (
                <img src={coverImageUrl} alt="Homepage cover preview" />
              ) : (
                <div className="cover-placeholder">No cover image set — the default image will be used.</div>
              )}
            </div>

            <div className="cover-actions">
              <FileButton
                variant="tint"
                icon={<Upload size={16} />}
                accept="image/*"
                onFiles={handleFileChange}
                loading={uploading}
                disabled={!canManage}
              >
                {uploading ? "Uploading…" : coverImageUrl ? "Replace image" : "Upload image"}
              </FileButton>
              {coverImageUrl && canManage && (
                <Button variant="ghost" icon={<X size={16} />} onClick={handleClear} disabled={uploading}>
                  Use default
                </Button>
              )}
            </div>

            <Field label="Image URL" htmlFor="cover-url">
              <Input
                id="cover-url"
                type="url"
                value={coverImageUrl}
                onChange={(e) => setCoverImageUrl(e.target.value)}
                placeholder="https://…"
                disabled={!canManage}
              />
            </Field>
          </section>

          <section className="config-section instapay-section">
            <h4 className="config-label">InstaPay Transfer Details</h4>
            <p className="config-hint">
              Where players send InstaPay payments. Shown when they pay by InstaPay (tournament registration, court
              booking, balance top-up, shop) and in payment emails. A tournament&rsquo;s own payment instructions still
              replace these for that tournament.
            </p>

            <Field
              label="Bank details"
              htmlFor="instapay-details"
              hint="One per line as Label: value, e.g. Bank: CIB. On the website each line gets its own Copy button."
            >
              <Textarea
                id="instapay-details"
                rows={5}
                maxLength={1000}
                value={instaPayDetails}
                onChange={(e) => setInstaPayDetails(e.target.value)}
                placeholder={"Bank: …\nAccount name: …\nAccount number: …\nIBAN: …"}
                disabled={!canManage}
              />
            </Field>
          </section>

          <section className="config-section clash-points-section">
            <h4 className="config-label">Clash Points</h4>
            <p className="config-hint">
              A loyalty currency users earn through activity and convert into wallet balance (EGP).
              These are separate from leaderboard ranking points. Any rate left at 0 keeps that part
              of the system inert until you set it.
            </p>

            <Switch
              id="points-enabled"
              checked={pointsEnabled}
              onChange={setPointsEnabled}
              disabled={!canManage}
              label="Clash Points enabled"
              description={
                <>
                  Master switch. When off, users stop earning points and can't convert them — existing
                  balances are kept but frozen until you turn it back on.
                </>
              }
            />

            <FormGrid columns={2} className="points-grid">
              {pointsField(
                "points-validity-months",
                "Validity (months)",
                pointsValidityMonths,
                setPointsValidityMonths,
                "1",
                <>
                  How long points stay usable. Each batch a user earns expires this many months
                  after it's earned, then it's lost.
                  <span className="points-example">
                    e.g. {pointsValidityMonths || "0"} → points earned today expire in{" "}
                    {pointsValidityMonths || "0"} month(s).
                  </span>
                </>
              )}
              {pointsField(
                "points-to-egp-rate",
                "Conversion rate (EGP per point)",
                pointsToEgpRate,
                setPointsToEgpRate,
                "0.01",
                <>
                  What one point is worth when a user cashes out to wallet balance. Set 0 to disable
                  conversion entirely.
                  <span className="points-example">
                    e.g. {pointsToEgpRate || "0"} → 100 points ={" "}
                    {(100 * (parseFloat(pointsToEgpRate) || 0)).toFixed(2)} EGP.
                  </span>
                </>
              )}
              {pointsField(
                "min-points-to-convert",
                "Minimum conversion (points)",
                minPointsToConvert,
                setMinPointsToConvert,
                "1",
                <>
                  The fewest points a user can convert in one transaction. Use 0 for no minimum.
                  <span className="points-example">
                    e.g. {minPointsToConvert || "0"} → users must convert at least{" "}
                    {minPointsToConvert || "0"} points at a time.
                  </span>
                </>
              )}
              {pointsField(
                "reservation-points-per-egp",
                "Reservation earn rate (points per EGP)",
                reservationPointsPerEgp,
                setReservationPointsPerEgp,
                "0.01",
                <>
                  Points a user earns for each 1 EGP spent on a court reservation (rounded down). Set
                  0 so reservations earn nothing.
                  <span className="points-example">
                    e.g. {reservationPointsPerEgp || "0"} → a 200 EGP booking earns{" "}
                    {Math.floor(200 * (parseFloat(reservationPointsPerEgp) || 0))} points.
                  </span>
                </>
              )}
              {pointsField(
                "registration-points-per-egp",
                "Registration earn rate (points per EGP)",
                registrationPointsPerEgp,
                setRegistrationPointsPerEgp,
                "0.01",
                <>
                  Points a player earns per 1 EGP of their tournament registration fee (rounded down).
                  Per-team fees are split across the team. Set 0 so registrations earn nothing.
                  <span className="points-example">
                    e.g. {registrationPointsPerEgp || "0"} → a 200 EGP registration earns{" "}
                    {Math.floor(200 * (parseFloat(registrationPointsPerEgp) || 0))} points.
                  </span>
                </>
              )}
            </FormGrid>

            <Alert tone="info" className="points-note">
              <strong>Note:</strong> the <strong>conversion rate</strong> is how much a point is
              worth when cashing <em>out</em>, while the <strong>reservation</strong> and{" "}
              <strong>registration</strong> earn rates are how many points are earned per EGP spent.
              Points for <strong>winning</strong> are set per tournament (on each category's
              prizes/awards), not here.
            </Alert>

            <div className="points-converter">
              <div className="converter-head">
                <h5 className="points-converter-title">Converter</h5>
                <p>Try any amount against the rates above. Uses your current (even unsaved) values.</p>
              </div>

              <div className="converter-calc">
                <label htmlFor="conv-points">Points → wallet balance</label>
                <div className="converter-line">
                  <Input
                    id="conv-points"
                    type="number"
                    inputSize="sm"
                    block={false}
                    className="converter-input"
                    min="0"
                    step="1"
                    value={convertPointsInput}
                    onChange={(e) => setConvertPointsInput(e.target.value)}
                    placeholder="e.g. 250"
                  />
                  <span className="converter-unit">pts</span>
                  <span className="converter-eq">=</span>
                  <span className="converter-result">
                    {((parseFloat(convertPointsInput) || 0) * (parseFloat(pointsToEgpRate) || 0)).toFixed(2)} EGP
                  </span>
                </div>
                {(parseFloat(convertPointsInput) || 0) > 0 &&
                  (parseFloat(convertPointsInput) || 0) < (parseInt(minPointsToConvert, 10) || 0) && (
                    <p className="converter-hint warn">
                      Below the {minPointsToConvert}-point minimum — a user couldn't convert this amount.
                    </p>
                  )}
                {(parseFloat(convertPointsInput) || 0) > 0 &&
                  (parseFloat(pointsToEgpRate) || 0) === 0 && (
                    <p className="converter-hint">Conversion rate is 0, so points convert to 0 EGP.</p>
                  )}
              </div>

              <div className="converter-calc">
                <label htmlFor="conv-cost">Reservation spend → points earned</label>
                <div className="converter-line">
                  <Input
                    id="conv-cost"
                    type="number"
                    inputSize="sm"
                    block={false}
                    className="converter-input"
                    min="0"
                    step="0.01"
                    value={convertCostInput}
                    onChange={(e) => setConvertCostInput(e.target.value)}
                    placeholder="e.g. 200"
                  />
                  <span className="converter-unit">EGP</span>
                  <span className="converter-eq">=</span>
                  <span className="converter-result">
                    {Math.floor((parseFloat(convertCostInput) || 0) * (parseFloat(reservationPointsPerEgp) || 0))} points
                  </span>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </Card>
  );
};

export default SiteConfigurationManagement;
